// v49.24 — Hybrid view-as override.
// Hybrid users can sit in coord-view or partner-view to see what their
// counterparts see. Stored in localStorage so it persists across
// sessions until the user flips it back. Emits an event on change so
// the Sidebar + Topbar + nav filters re-render in sync.

import { useEffect, useState } from "react";
import { useShellUser } from "@/features/auth/AuthContext";
import type { Role } from "@/lib/api/types";

const STORAGE_KEY = "lc_role_view_v1";

export type RoleOverride = "coordinator" | "partner" | null;

export function readOverride(): RoleOverride {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === "coordinator" || v === "partner" ? v : null;
  } catch { return null; }
}

export function writeOverride(o: RoleOverride) {
  try {
    if (o == null) localStorage.removeItem(STORAGE_KEY);
    else           localStorage.setItem(STORAGE_KEY, o);
    window.dispatchEvent(new CustomEvent("lc-role-override-changed"));
  } catch { /* ignore */ }
}

/* The role the UI should treat the user as. For hybrid users with an
   active override, return the overridden role. For everyone else,
   return the user's real role. Returns null when no user. */
export function useEffectiveRole(): Role | null {
  const user = useShellUser();
  const [override, setOverride] = useState<RoleOverride>(() => readOverride());
  useEffect(() => {
    const refresh = () => setOverride(readOverride());
    window.addEventListener("lc-role-override-changed", refresh);
    return () => window.removeEventListener("lc-role-override-changed", refresh);
  }, []);
  if (!user) return null;
  if (user.role !== "hybrid") return user.role;
  return override || "hybrid";
}

/* Reader hook for components that need to render the toggle. */
export function useRoleOverride(): [RoleOverride, (next: RoleOverride) => void] {
  const [override, setOverride] = useState<RoleOverride>(() => readOverride());
  useEffect(() => {
    const refresh = () => setOverride(readOverride());
    window.addEventListener("lc-role-override-changed", refresh);
    return () => window.removeEventListener("lc-role-override-changed", refresh);
  }, []);
  return [override, (next) => { writeOverride(next); setOverride(next); }];
}
